import { useCallback, useMemo } from "react";
import { ArrowUp, Link as LinkIcon, MessageSquare } from "lucide-react";

import type { ApiConversation, ApiMessage, ApiUser } from "../lib/api";
import type { ConversationIndexEntry, SlackUser, UsersMap } from "../types/slack";
import { Attachment } from "./Attachment";
import { Tooltip } from "./ui/Tooltip";
import { useToast } from "./ui/Toast";
import { formatTsDay, formatTsLong, formatTsShort } from "../lib/format";
import { isReplyBroadcast, previewParentText } from "../lib/broadcast";
import {
  isMessageMarkedDeleted,
  isMessageMarkedEdited,
} from "../lib/messageFlags";
import { renderSlackText } from "../lib/slack-text";
import { resolveAvatarPaletteIndex } from "../lib/avatar";
import { avatarGradientCSS } from "../lib/avatarGradient";

// Aurora — reaction pills. Slack reaction names → a small map of the
// common emoji; anything unknown falls back to :name: (mono), which
// is still informative. Skin-tone suffixes are stripped.
const REACTION_EMOJI: Record<string, string> = {
  "+1": "👍", "-1": "👎", thumbsup: "👍", thumbsdown: "👎",
  heart: "❤️", hearts: "💕", heart_eyes: "😍", joy: "😂",
  smile: "😄", grinning: "😀", laughing: "😆", rofl: "🤣",
  white_check_mark: "✅", heavy_check_mark: "✔️", x: "❌",
  tada: "🎉", confetti_ball: "🎊", clap: "👏", raised_hands: "🙌",
  pray: "🙏", fire: "🔥", rocket: "🚀", eyes: "👀", "100": "💯",
  thinking_face: "🤔", wave: "👋", ok_hand: "👌", muscle: "💪",
  star: "⭐", star2: "🌟", sparkles: "✨", bulb: "💡",
  warning: "⚠️", question: "❓", exclamation: "❗",
  cry: "😢", sob: "😭", sweat_smile: "😅", grimacing: "😬",
  partying_face: "🥳", saluting_face: "🫡", handshake: "🤝",
  green_heart: "💚", blue_heart: "💙", purple_heart: "💜",
};
function reactionGlyph(name: string): string {
  const base = name.split("::")[0]!; // strip ::skin-tone-N
  return REACTION_EMOJI[base] ?? `:${base}:`;
}

// Slug-y color names for the data-avatar-color attribute. Index
// order matches the --theme-avatar-N-bg tokens in lib/theme.ts.
const AVATAR_COLOR_NAMES = [
  "deep-aubergine",
  "slack-blue",
  "forest-green",
  "amber",
  "muted-brick",
  "violet",
] as const;

function initialsOf(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface ResolvedUser {
  displayName: string;
  initials: string;
}

function resolveUser(
  users: Record<string, ApiUser>,
  userID: string | undefined,
): ResolvedUser {
  const u = userID ? users[userID] : undefined;
  // Fallback chain per spec: real_name → display_name → name → user_id.
  const displayName =
    u?.real_name || u?.display_name || u?.name || userID || "Unknown";
  return { displayName, initials: initialsOf(displayName) || "??" };
}

interface MessageProps {
  /** team_id needed by the Attachment component to build API URLs. */
  teamID: string;
  msg: ApiMessage;
  /** Workspace users dict for sender resolution (real_name etc).
   *  Empty map is OK — falls back to user_id then "Unknown". */
  users: Record<string, ApiUser>;
  /** Conversation list for #channel mention resolution in body
   *  text. Service returns ApiConversation; the legacy slack-text
   *  renderer expects ConversationIndexEntry — we adapt. */
  conversations: ApiConversation[];
  /** user_id → 0..5 palette index. Pre-built by the parent so
   *  every Message in a conversation shares the same map without
   *  recomputing per row. */
  avatarPaletteByUserID: Record<string, number>;
  /** True for the parent of the currently-open thread drawer. */
  isThreadParent?: boolean;
  /** True for thread replies inside ThreadDrawer. */
  indented?: boolean;
  /** True for messages immediately following one from the same
   *  sender within the grouping window. */
  groupedContinuation?: boolean;
  /** Pass 2.13.5 (BUG 1 fix): when true, this row is the
   *  search-jump target and renders the transient amber
   *  highlight + left-border accent. Must flow through React's
   *  render so it survives reconciliation when the around-fetch
   *  resolves and unmount/remounts the row — the pre-fix
   *  classList.add was erased on remount. */
  isHighlighted?: boolean;
  /** Pass 6.0b.3 — for a thread_broadcast row, the parent
   *  message looked up from the same conversation. ``undefined``
   *  means the parent isn't in the loaded window (the renderer
   *  falls back to "(parent unavailable)" with no click-through).
   *  Ignored for non-broadcast rows. */
  broadcastParent?: ApiMessage;
  onOpenThread: (ts: string) => void;
  /** S4.0: conversation id — needed to build a message permalink in
   *  the hover toolbar. When omitted (e.g. inside ThreadDrawer) the
   *  toolbar is suppressed. */
  convID?: string;
  /** S4.0: jump to + highlight a thread reply's parent in the main
   *  pane. Parent wires this to the scroll-to-ts machinery. */
  onGoToParent?: (parentTs: string) => void;
}

export function Message({
  teamID,
  msg,
  users,
  conversations,
  avatarPaletteByUserID,
  isThreadParent = false,
  indented = false,
  groupedContinuation = false,
  isHighlighted = false,
  broadcastParent,
  onOpenThread,
  convID,
  onGoToParent,
}: MessageProps) {
  const { toast } = useToast();
  // Pass 6.0b.3 — render the "replied to a thread:" header only on
  // top-level broadcast rows. ``indented`` means we're inside the
  // thread drawer; the broadcast already appears there as a normal
  // reply and the header would be redundant.
  const showBroadcastHeader = !indented && isReplyBroadcast(msg);
  const broadcastParentTs = showBroadcastHeader
    ? (msg.thread_ts ?? null)
    : null;
  const { displayName, initials } = useMemo(
    () => resolveUser(users, msg.user ?? undefined),
    [users, msg.user],
  );
  const avatarIdx = useMemo(
    () =>
      resolveAvatarPaletteIndex(
        avatarPaletteByUserID,
        msg.user ?? undefined,
        displayName,
      ),
    [avatarPaletteByUserID, msg.user, displayName],
  );
  const replyCount = msg.replies?.length ?? 0;
  const hasReplies = !indented && replyCount > 0;

  // S4.0: hover toolbar. Only on main-pane rows that know their
  // conversation id (ThreadDrawer renders Message without convID).
  const isThreadReply =
    typeof msg.thread_ts === "string" && msg.thread_ts !== msg.ts;
  const showToolbar = !indented && !!convID;

  const copyPermalink = useCallback(async () => {
    const url =
      `${window.location.origin}/browse/${encodeURIComponent(teamID)}` +
      `/${encodeURIComponent(convID ?? "")}?ts=${encodeURIComponent(msg.ts)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Permalink copied", type: "success" });
    } catch {
      toast({ title: "Couldn't copy", type: "error" });
    }
  }, [teamID, convID, msg.ts, toast]);

  return (
    <div
      data-testid="message-row"
      data-message-ts={msg.ts}
      data-message-user={msg.user ?? ""}
      data-thread-parent={isThreadParent ? "true" : "false"}
      data-indented={indented ? "true" : "false"}
      data-avatar-index={avatarIdx}
      data-grouped-continuation={groupedContinuation ? "true" : "false"}
      data-message-deleted={isMessageMarkedDeleted(msg) ? "true" : "false"}
      data-message-edited={isMessageMarkedEdited(msg) ? "true" : "false"}
      className={[
        "group relative flex gap-3 px-6 transition-colors duration-150",
        groupedContinuation ? "py-0.5" : "py-2",
        indented ? "ml-6" : "",
        isThreadParent
          ? "bg-theme-bg-thread-highlight"
          : "hover:bg-theme-bg-message-hover",
        // Pass 6.0c.3: deleted rows render with a [deleted]
        // placeholder for the body (see below) instead of the
        // pre-fix opacity dim — matches Slack's own UI. No
        // row-level opacity here; the placeholder's italic
        // muted text is the only visual cue.
        // Pass 2.13.5: search-jump highlight, expressed in React's
        // render output so it survives reconciliation. CSS rule in
        // globals.css.
        isHighlighted ? "message-row-highlight" : "",
      ].join(" ")}
    >
      {/* S4.0: hover toolbar — top-right, fades in on row hover. */}
      {showToolbar && (
        <div
          data-testid="message-toolbar"
          className="absolute top-1 right-3 z-10 flex items-center gap-tight px-1 py-1 bg-theme-bg-elevated/95 backdrop-blur-md border border-white/[0.1] shadow-2xl rounded-xl opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200"
        >
          <Tooltip content="Copy link to this message" placement="top">
            <button
              type="button"
              onClick={copyPermalink}
              data-testid="msg-copy-permalink"
              aria-label="Copy link to this message"
              className="w-6 h-6 flex items-center justify-center text-theme-text-tertiary hover:text-theme-text-primary hover:bg-theme-bg-conv-hover rounded-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
            >
              <LinkIcon className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
            </button>
          </Tooltip>
          {hasReplies && (
            <Tooltip content={`View thread (${replyCount} repl${replyCount === 1 ? "y" : "ies"})`} placement="top">
              <button
                type="button"
                onClick={() => onOpenThread(msg.ts)}
                data-testid="msg-view-thread"
                aria-label="View thread"
                className="w-6 h-6 flex items-center justify-center text-theme-text-tertiary hover:text-theme-text-primary hover:bg-theme-bg-conv-hover rounded-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
              >
                <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
              </button>
            </Tooltip>
          )}
          {isThreadReply && onGoToParent && (
            <Tooltip content="Go to parent message" placement="top">
              <button
                type="button"
                onClick={() => onGoToParent(msg.thread_ts!)}
                data-testid="msg-go-to-parent"
                aria-label="Go to parent message"
                className="w-6 h-6 flex items-center justify-center text-theme-text-tertiary hover:text-theme-text-primary hover:bg-theme-bg-conv-hover rounded-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
              >
                <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
              </button>
            </Tooltip>
          )}
        </div>
      )}
      {groupedContinuation ? (
        // Pass 6.0c.7: continuation rows replace the avatar with a
        // muted ts in the left gutter. Render the (edited) marker
        // inline below the ts on these rows — the only visible
        // header element on a continuation row. Pre-fix the
        // marker was scoped to the ungrouped name+ts block so
        // continuation rows silently dropped it; the message
        // body still updated to the edited text but the operator
        // had no visual cue. Mirrors Slack's own UI: a hover-
        // ts continuation message that's been edited still
        // shows "(edited)" below the ts.
        <div
          data-avatar-index={avatarIdx}
          data-avatar-color={AVATAR_COLOR_NAMES[avatarIdx]}
          className="w-9 flex-shrink-0 flex flex-col items-center pt-1 select-none gap-0.5"
        >
          <span
            className="font-mono text-[10px] text-theme-text-tertiary leading-none"
            title={formatTsLong(msg.ts)}
          >
            {formatTsShort(msg.ts)}
          </span>
          {isMessageMarkedEdited(msg) && (
            <span
              data-testid="message-edited-marker"
              className="font-mono text-[9px] text-theme-text-tertiary italic leading-none"
              title={
                msg.edited_at
                  ? `edited at ${formatTsLong(msg.edited_at)}`
                  : "edited"
              }
            >
              (edited)
            </span>
          )}
        </div>
      ) : (
        <div
          data-avatar-index={avatarIdx}
          data-avatar-color={AVATAR_COLOR_NAMES[avatarIdx]}
          className="w-9 h-9 flex items-center justify-center font-semibold text-xs flex-shrink-0 shadow-sm"
          style={{
            background: avatarGradientCSS(avatarIdx),
            color: "var(--theme-avatar-fg)",
            borderRadius: "12px",
          }}
        >
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {!groupedContinuation && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-theme-user-name text-theme-text-primary text-theme-user-name">
              {displayName}
            </span>
            <span
              className="text-theme-timestamp text-theme-text-timestamp"
              title={formatTsLong(msg.ts)}
            >
              {formatTsShort(msg.ts)}
            </span>
            {/* Pass 6.0c.3: only the (edited) marker stays inline
                with the timestamp. (deleted) is no longer a
                marker — the entire body collapses to a [deleted]
                placeholder below. */}
            {isMessageMarkedEdited(msg) && (
              <span
                data-testid="message-edited-marker"
                className="text-theme-timestamp text-theme-text-tertiary italic font-mono text-[11px]"
                title={
                  msg.edited_at
                    ? `edited at ${formatTsLong(msg.edited_at)}`
                    : "edited"
                }
              >
                (edited)
              </span>
            )}
          </div>
        )}
        {/* Pass 6.0c.3: deleted=true → muted [deleted] placeholder,
            no body, no attachments, no thread-reply button. Sender +
            timestamp stay (rendered above) so the deletion is
            contextual: the operator sees "Omar deleted a message at
            14:23" the way Slack itself does. */}
        {isMessageMarkedDeleted(msg) ? (
          <div
            data-testid="message-deleted-placeholder"
            className={[
              "italic text-theme-text-tertiary text-theme-message",
              groupedContinuation ? "" : "mt-0.5",
            ].join(" ")}
          >
            [deleted]
          </div>
        ) : (
          <>
            {showBroadcastHeader && broadcastParentTs && (
              <BroadcastHeader
                parent={broadcastParent}
                parentTs={broadcastParentTs}
                users={users}
                conversations={conversations}
                onOpenThread={onOpenThread}
              />
            )}
            {msg.text && (
              <div
                className={[
                  "text-theme-text-body text-theme-message break-words [overflow-wrap:anywhere] min-w-0",
                  groupedContinuation ? "" : "mt-0.5",
                ].join(" ")}
              >
                {renderText(msg.text, users, conversations)}
              </div>
            )}
            {(msg.files ?? []).length > 0 && (
              // Aurora: attachments STACK as blocks. Pre-fix the cards
              // were inline-flex next to inline image anchors, so an
              // image + a file card shared one text line, baseline-
              // aligned — the image floated mid-pane with the card
              // hanging at its bottom-left.
              <div className="mt-1 flex flex-col items-start gap-1.5">
                {(msg.files ?? []).map((f, i) => (
                  <Attachment
                    key={f.id || `${msg.ts}-${i}`}
                    teamID={teamID}
                    fileRef={f}
                  />
                ))}
              </div>
            )}
            {/* Aurora — reaction pills. The archive records reactions
                but pre-redesign never rendered them. Read-only. */}
            {(msg.reactions ?? []).length > 0 && (
              <div
                data-testid="message-reactions"
                className="mt-1.5 flex flex-wrap items-center gap-1.5"
              >
                {(msg.reactions ?? []).map((r) => (
                  <span
                    key={r.name}
                    data-testid={`reaction-pill-${r.name}`}
                    title={`:${r.name}:`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.09] text-[12px] leading-5"
                  >
                    <span aria-hidden>{reactionGlyph(r.name)}</span>
                    <span className="font-mono text-[11px] text-theme-text-secondary">
                      {r.count}
                    </span>
                  </span>
                ))}
              </div>
            )}
            {hasReplies && (
              <button
                type="button"
                onClick={() => onOpenThread(msg.ts)}
                data-testid={`thread-reply-button-${msg.ts}`}
                className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white/[0.03] border border-white/[0.07] hover:border-theme-accent-link/50 hover:bg-white/[0.05] transition-colors text-[11.5px]"
              >
                <span className="font-semibold text-theme-accent-link">
                  {replyCount} {replyCount === 1 ? "reply" : "replies"}
                </span>
                <span className="font-mono text-[10px] text-theme-text-muted">
                  view thread →
                </span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Adapt ApiUser map → UsersMap shape that renderSlackText expects.
// The mention/channel renderer reads display_name, name, and id;
// the rest of the SlackUser shape isn't touched. Cheap shallow
// pass per render but only when text contains a mention.
function adaptUsers(users: Record<string, ApiUser>): UsersMap {
  const out: UsersMap = {};
  for (const [id, u] of Object.entries(users)) {
    out[id] = {
      id,
      name: u.name ?? "",
      real_name: u.real_name ?? null,
      display_name: u.display_name ?? null,
      email: null,
      is_bot: u.is_bot ?? false,
      deleted: u.deleted ?? false,
      team_id: "",
    } as SlackUser;
  }
  return out;
}

function adaptConversations(
  convs: ApiConversation[],
): ConversationIndexEntry[] {
  return convs.map((c) => ({
    id: c.id,
    type: c.type,
    label: c.label,
    member_count: null,
    message_count: c.message_count,
    thread_reply_count: 0,
    is_member: false,
    not_joined: false,
  }));
}

function renderText(
  text: string,
  users: Record<string, ApiUser>,
  conversations: ApiConversation[],
) {
  return (
    <span className="whitespace-pre-wrap">
      {renderSlackText(text, adaptUsers(users), adaptConversations(conversations))}
    </span>
  );
}

interface BroadcastHeaderProps {
  /** The parent message looked up by the caller (MessagePane has
   *  the whole conversation in memory and joins on ts === thread_ts).
   *  ``undefined`` means the parent isn't in the loaded window — we
   *  render the fallback ("parent unavailable") with no
   *  click-through. */
  parent: ApiMessage | undefined;
  /** Always present; used both as the click-through target (so the
   *  thread drawer opens on the right ts) and as the data-attribute
   *  for tests. */
  parentTs: string;
  users: Record<string, ApiUser>;
  /** Pass 6.0c — needed by ``previewParentText`` for
   *  <#CHANNEL_ID> substitution. Empty array is OK. */
  conversations: ApiConversation[];
  onOpenThread: (ts: string) => void;
}

// Pass 6.0b.3 — Slack-style "replied to a thread:" header. Sits
// above the broadcast's message body inside the same row. Clicking
// opens the thread drawer on the parent (re-using the existing
// onOpenThread plumbing the "↳ N replies" button uses). When the
// parent isn't in the loaded window (rare: very old parent, or
// archive started after the parent was posted), fall back to a
// non-clickable, no-preview header so the user still sees the
// thread relationship.
function BroadcastHeader({
  parent,
  parentTs,
  users,
  conversations,
  onOpenThread,
}: BroadcastHeaderProps) {
  const parentAvailable = parent !== undefined;
  const previewText = parentAvailable
    ? previewParentText(parent.text, users, conversations)
    : "";
  const parentSender = parentAvailable
    ? resolveUser(users, parent.user ?? undefined).displayName
    : null;

  const headerBody = parentAvailable ? (
    <>
      <span className="font-mono text-theme-text-tertiary">
        ↩ replied to a thread:
      </span>
      <span
        className="ml-2 text-theme-text-secondary truncate"
        data-testid="broadcast-parent-preview"
      >
        {parentSender && (
          <span className="font-theme-user-name text-theme-text-primary mr-1">
            {parentSender}:
          </span>
        )}
        {previewText}
      </span>
    </>
  ) : (
    <span
      className="font-mono text-theme-text-tertiary"
      data-testid="broadcast-parent-unavailable"
    >
      ↩ replied to a thread (parent unavailable)
    </span>
  );

  const className = [
    "mt-0.5 mb-1 px-3 py-1.5 flex items-baseline gap-1 min-w-0",
    "border-l-2 border-theme-border-default bg-theme-bg-message-hover",
    "text-[12px] leading-tight",
    parentAvailable
      ? "hover:bg-theme-bg-thread-highlight cursor-pointer transition-colors duration-150"
      : "",
  ].join(" ");

  if (!parentAvailable) {
    return (
      <div
        data-testid="broadcast-header"
        data-broadcast-parent-ts={parentTs}
        data-broadcast-parent-available="false"
        className={className}
      >
        {headerBody}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="broadcast-header"
      data-broadcast-parent-ts={parentTs}
      data-broadcast-parent-available="true"
      onClick={() => onOpenThread(parentTs)}
      className={`${className} text-left w-full`}
    >
      {headerBody}
    </button>
  );
}

interface DateSeparatorProps {
  ts: string;
}

export function DateSeparator({ ts }: DateSeparatorProps) {
  return (
    <div
      data-testid="date-separator"
      data-day={ts}
      className="px-6 py-3 flex items-center gap-3"
    >
      <div className="flex-1 border-t border-theme-border-divider" />
      <span
        data-testid="date-separator-pill"
        className="text-theme-date-pill-text bg-theme-date-pill-bg whitespace-nowrap"
        style={{
          fontSize: "var(--theme-date-pill-font-size)",
          fontWeight: "var(--theme-date-pill-font-weight)",
          padding: "var(--theme-date-pill-padding)",
          borderRadius: "var(--theme-date-pill-radius)",
          border: "var(--theme-date-pill-border)",
          lineHeight: "1.6",
        }}
      >
        {formatTsDay(ts)}
      </span>
      <div className="flex-1 border-t border-theme-border-divider" />
    </div>
  );
}
